import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonImg,
  IonMenuToggle,
  IonTitle,
  IonToolbar,
  useIonModal,
} from '@ionic/react';
import { useSelector } from 'react-redux';
import { IState, IUserInfo } from 'appRedux/createStore';
import { arrowBackOutline, searchOutline } from 'ionicons/icons';
import { S3_PROFILE_BASE as S3_BASE } from 'common/common';
import useWhiteLabelStyles, { WHITE_LABEL_TYPE_ENUM } from '../../hooks/useWhiteLabelStyles';

import { useHistory, useLocation } from 'react-router';
import { SearchModal } from './searchModal';
import useSlotDetailHistory from 'globalServices/useSlotDetailHistory';
import { casinoIdListEnum } from 'common/commonEnum';
import { useEffect, useState } from 'react';
import SlotCheckSearchIcon from 'components/SlotCheckSearchIcon';
import { UNIVERSAL_SEARCH_ROUTE, isSlotCheckSearchEnabled } from 'lib/slotCheckSearchAccess';
import { hasActiveUniversalSearch, subscribeUniversalSearch } from 'lib/universalSearchSession';
import { trackUsGlobeTapped } from 'lib/universalSearchAnalytics';

interface ICasinoNavBar {
  showBack?: boolean;
  searchBorder?: boolean;
  hideSearch?: boolean;
  hideMenu?: boolean;
  isDarkMode?: boolean;
  styles?: object;
  hideLogo?: boolean;
  hideSlotCheckLogo?: boolean;
  headerBackgroundUrl?: string;
}

const CasinoNavBar = ({
  showBack,
  hideSearch,
  hideMenu = false,
  isDarkMode = false,
  styles,
  hideLogo = false,
  hideSlotCheckLogo = false,
  headerBackgroundUrl,
}: ICasinoNavBar) => {
  const btnstyles = useWhiteLabelStyles(WHITE_LABEL_TYPE_ENUM.BUTTON);
  const iconDarMode = isDarkMode ? { color: '#000' } : { color: '#fff' };
  const casinoNavBarImageAlt = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.logos?.logoAlt
  ) as string;
  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;

  const { avatarId, avatarBgId, aliasInitial2, slotCheckSearchEnabled } = useSelector<IState>(
    (state) => state.app.persistedState.userInfo
  ) as IUserInfo;
  // Slot Check Search globe: only for users.slotCheckSearchEnabled = 1 / "1".
  // Gold while this session has a saved search, white otherwise.
  const canSlotCheckSearch = isSlotCheckSearchEnabled(slotCheckSearchEnabled);
  const [hasSavedSearch, setHasSavedSearch] = useState<boolean>(() => hasActiveUniversalSearch());
  useEffect(() => {
    if (!canSlotCheckSearch) return undefined;
    setHasSavedSearch(hasActiveUniversalSearch());
    return subscribeUniversalSearch(() => setHasSavedSearch(hasActiveUniversalSearch()));
  }, [canSlotCheckSearch]);

  const history = useHistory();
  const location = useLocation();
  const isChooseCasinos =
    location.pathname === '/auth/choosecasinos' ||
    location.pathname === UNIVERSAL_SEARCH_ROUTE ||
    location.pathname === '/tabs/casinos' ||
    location.pathname === '/auth/login' ||
    location.pathname === '/auth/casinoadmin' ||
    location.pathname === '/auth/createaccount' ||
    location.pathname === '/auth/forgotpassword' ||
    location.pathname === '/auth/enterprise-forgotpassword' ||
    location.pathname === '/auth/enterprise-resetpassword' ||
    location.pathname === '/changepassword';
  const menuIconStyle = isChooseCasinos ? { color: '#94A0AA' } : iconDarMode;
  const { addToSlotHistory } = useSlotDetailHistory();

  const goSelectCasino = () => {
    // forceSelect clears any pending resume so picking a different casino
    // is not immediately bounced back into the last-viewed one.
    history.push('/auth/choosecasinos?forceSelect=1');
  };

  const showCasinoLogo =
    !isChooseCasinos &&
    !hideLogo &&
    Boolean(casinoNavBarImageAlt) &&
    (casinoId === casinoIdListEnum.LOTC ||
      casinoId === casinoIdListEnum.EUREKA ||
      casinoId === casinoIdListEnum.JJC ||
      casinoId === casinoIdListEnum.RWC ||
      casinoId === casinoIdListEnum.SSC ||
      casinoId === casinoIdListEnum.ACH);

  const [showSearchModal, hideSearchModal] = useIonModal(SearchModal, {
    title: 'Slot Search',
    onClickIndividualItem: (slotNumber: number) => {
      hideSearchModal();
      addToSlotHistory(slotNumber);
      // history.push('/detail');
    },
    closeHandler: () => hideSearchModal(),
  });

  const casinoHeaderClass = (() => {

    if (isChooseCasinos) return 'casino-header--choose-casinos';

    
    switch (Number(casinoId)) {
      case casinoIdListEnum.LOTC: return 'casino-header--lotc';
      case casinoIdListEnum.EUREKA: return 'casino-header--eureka';
      case casinoIdListEnum.JJC: return 'casino-header--lotc';
      case casinoIdListEnum.RWC: return 'casino-header--rwc';
      case casinoIdListEnum.SSC: return 'casino-header--ssc';
      case casinoIdListEnum.ACH: return 'casino-header--rwc';
      default: return 'casino-header--default';
    }
  })();

  return (
    <div
      className={`slotcheck-header ${casinoHeaderClass}${headerBackgroundUrl ? ' casino-header--custom' : ''}`}
      style={headerBackgroundUrl ? ({ '--custom-header-bg': `url('${headerBackgroundUrl}')` } as React.CSSProperties) : undefined}
    >
      <IonHeader className="app-header ion-no-border">
        <IonToolbar
          className="ion-no-border no-border"
          style={styles}
        >
          <IonButtons slot="start">
            {showBack && (
              <IonButton
                fill="clear"
                onClick={() => {
                  history.goBack();
                }}
                className="icon-back"
                style={btnstyles}
                slot="start"
              >
                <IonIcon style={btnstyles} icon={arrowBackOutline} />
              </IonButton>
            )} 
          </IonButtons>
          <div className="casino-logo-header">
            {showCasinoLogo && (
              <button
                type="button"
                className="casino-logo-button"
                onClick={goSelectCasino}
                aria-label="Select Casino"
              >
                <IonImg
                  src={casinoNavBarImageAlt}
                  className={`casino-logo${casinoId === casinoIdListEnum.ACH ? ' casino-logo--ach' : ''}`}
                />
              </button>
            )}
          </div>
          <IonTitle>
            {isChooseCasinos && !hideSlotCheckLogo && (
              <div className="navbar-slotcheck-logo-wrapper">
                <IonImg src="assets/images/SlotCheck-White.png" className="navbar-slotcheck-logo" />
              </div>
            )}
          </IonTitle>

          <IonButtons slot="end">
            {/* Slot Check Search: left of the casino search icon, casino headers only. */}
            {!hideSearch && !isChooseCasinos && canSlotCheckSearch && (
              <IonButton
                className="slot-check-search-button"
                aria-label={hasSavedSearch ? 'Back to Slot Check Search' : 'Slot Check Search'}
                onClick={() => {
                  trackUsGlobeTapped(hasSavedSearch);
                  history.push(UNIVERSAL_SEARCH_ROUTE);
                }}
              >
                <SlotCheckSearchIcon
                  active={hasSavedSearch}
                  className={`slot-check-search-icon${hasSavedSearch ? ' slot-check-search-icon--active' : ''}`}
                />
              </IonButton>
            )}
            {!hideSearch && (
              <IonButton
                onClick={() => {
                  showSearchModal({ cssClass: 'slot-search-ion-modal' });
                }}
              >
                <IonIcon icon={searchOutline} className="search-icon" />
              </IonButton>
            )}
            {!hideMenu && (
              <IonMenuToggle>
                <IonButton className={isChooseCasinos ? 'menu-bg' : ''}>
                  {isChooseCasinos ? (
                    <img src="assets/icon/RoundMenu2.png" alt="Menu" className="nav-round-menu" />
                  ) : avatarId ? (
                    <div
                      className="nav-avatar"
                      style={{ backgroundImage: `url(${S3_BASE}/avatar-bg${avatarBgId}.png)` }}
                    >
                      <img
                        src={`${S3_BASE}/avatar${avatarId}.png`}
                        alt="profile"
                        className="nav-avatar__img"
                      />
                    </div>
                  ) : (
                    <div className="nav-avatar nav-avatar--initial">
                      {aliasInitial2 || ''}
                    </div>
                  )}
                </IonButton>
              </IonMenuToggle>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
    </div>
  );
};

export default CasinoNavBar;
